import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle2, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { Button } from './ui/Button';
interface PrePaymentFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: FormData) => void;
  ticketName: string;
  ticketPrice: string;
  ticketType?: 'festival-only' | 'accommodation' | 'double-alone';
}
interface FormData {
  fullName: string;
  phone: string;
  email: string;
  gender: 'male' | 'female' | '';
  nightChoice: 'thursday' | 'friday' | '';
  roomType: 'double' | 'triple' | '';
  sharedRoomAcknowledgment: boolean;
  roommate1Name: string;
  roommate2Name: string;
  companionName: string;
  companionPhone: string;
  companionGender: 'male' | 'female' | '';
  competitiveTrack: string[];
  rowingType: 'sup' | 'race' | 'surfski' | 'surfer' | 'atmosphere' | '';
  belongsToClub: 'yes' | 'no' | '';
  clubName: string;
  rentBoard: 'yes' | 'no' | '';
  healthDeclaration: boolean;
  medicalCheckups: boolean;
  riskAwareness: boolean;
  liabilityWaiver: boolean;
  medicalTreatment: boolean;
  photographyConsent: boolean;
  cancellationPolicy: boolean;
  whatsappGroup: boolean;
}
export function WaterTribeFestPrePaymentForm({
  isOpen,
  onClose,
  onSubmit,
  ticketName,
  ticketPrice,
  ticketType = 'accommodation'
}: PrePaymentFormProps) {
  const [showCancellationPolicy, setShowCancellationPolicy] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    phone: '',
    email: '',
    gender: '',
    nightChoice: '',
    roomType: '',
    sharedRoomAcknowledgment: false,
    roommate1Name: '',
    roommate2Name: '',
    companionName: '',
    companionPhone: '',
    companionGender: '',
    competitiveTrack: [],
    rowingType: '',
    belongsToClub: '',
    clubName: '',
    rentBoard: '',
    healthDeclaration: false,
    medicalCheckups: false,
    riskAwareness: false,
    liabilityWaiver: false,
    medicalTreatment: false,
    photographyConsent: false,
    cancellationPolicy: false,
    whatsappGroup: false
  });
  const isFormValid = () => {
    const basicFieldsValid = formData.fullName.trim() !== '' && formData.phone.trim() !== '' && formData.email.trim() !== '' && formData.gender !== '';
    // Companion name required for tickets with companion
    const companionFieldsValid = ticketName.includes('+ כרטיס מלווה') ? formData.companionName.trim() !== '' && formData.companionPhone.trim() !== '' && formData.companionGender !== '' : true;
    // Night choice required only for "one night" ticket
    const nightChoiceValid = ticketName.includes('לילה אחד') ? formData.nightChoice !== '' : true;
    // Room fields only required for accommodation tickets (not for festival-only or double-alone)
    // Skip roommate validation for tickets with companion
    const roomFieldsValid = ticketType === 'accommodation' && !ticketName.includes('+ כרטיס מלווה') ? formData.roomType !== '' && formData.sharedRoomAcknowledgment && formData.roommate1Name.trim() !== '' : ticketType === 'accommodation' && ticketName.includes('+ כרטיס מלווה') ? formData.roomType !== '' : true;
    const declarationsValid = formData.healthDeclaration && formData.medicalCheckups && formData.riskAwareness && formData.liabilityWaiver && formData.medicalTreatment && formData.photographyConsent && formData.cancellationPolicy;
    return basicFieldsValid && companionFieldsValid && nightChoiceValid && roomFieldsValid && declarationsValid;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isFormValid()) {
      onSubmit(formData);
    }
  };
  const handleCheckboxChange = (field: keyof FormData) => {
    setFormData(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };
  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  return <AnimatePresence data-id="element-315">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[80]" onClick={onClose} data-id="element-316" />

          <div className="fixed inset-0 z-[90] overflow-y-auto" dir="ltr" data-id="element-317">
            <div className="min-h-full flex items-center justify-center p-4 md:p-8" data-id="element-318">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} className="w-full max-w-4xl bg-white rounded-3xl shadow-2xl overflow-hidden" dir="rtl" onClick={e => e.stopPropagation()} data-id="element-319">
                <div className="flex-1 overflow-y-auto" data-id="element-320">
                  {/* Header */}
                  <div className="sticky top-0 bg-gradient-to-br from-indigo-600 to-purple-600 text-white p-6 md:p-8 z-10 shadow-lg" data-id="element-321">
                    <button onClick={onClose} className="absolute top-4 left-4 md:top-6 md:left-6 bg-white/20 hover:bg-white/30 rounded-full p-2 transition-colors" data-id="element-322">
                      <X className="w-5 h-5 md:w-6 md:h-6" data-id="element-323" />
                    </button>
                    <h2 className="text-2xl md:text-3xl font-bold mb-3" data-id="element-324">
                      טופס הרשמה לפסטיבל
                    </h2>
                    <div className="flex items-center justify-between text-sm md:text-base bg-white/20 backdrop-blur-sm rounded-xl p-4" data-id="element-325">
                      <span className="font-medium" data-id="element-326">{ticketName}</span>
                      <span className="font-bold text-lg" data-id="element-327">₪{ticketPrice}</span>
                    </div>
                  </div>

                  {/* Form */}
                  <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-8" data-id="element-328">
                    {/* Personal Information */}
                    <div className="space-y-4" data-id="element-329">
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-3 pb-3 border-b-2 border-indigo-100" data-id="element-330">
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-100 rounded-full flex items-center justify-center shrink-0" data-id="element-331">
                          <span className="text-indigo-600 font-bold text-sm md:text-base" data-id="element-332">
                            1
                          </span>
                        </div>
                        פרטים אישיים
                      </h3>

                      <div className="space-y-4" data-id="element-333">
                        <div data-id="element-334">
                          <label className="block text-sm md:text-base font-medium text-gray-700 mb-2" data-id="element-335">
                            שם מלא <span className="text-red-500" data-id="element-336">*</span>
                          </label>
                          <input type="text" required value={formData.fullName} onChange={e => handleInputChange('fullName', e.target.value)} className="w-full px-4 py-3 md:py-4 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all" placeholder="הזן שם מלא" data-id="element-337" />
                        </div>

                        <div data-id="element-338">
                          <label className="block text-sm md:text-base font-medium text-gray-700 mb-2" data-id="element-339">
                            טלפון <span className="text-red-500" data-id="element-340">*</span>
                          </label>
                          <input type="tel" required value={formData.phone} onChange={e => handleInputChange('phone', e.target.value)} className="w-full px-4 py-3 md:py-4 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all" placeholder="050-1234567" data-id="element-341" />
                        </div>

                        <div data-id="element-342">
                          <label className="block text-sm md:text-base font-medium text-gray-700 mb-2" data-id="element-343">
                            אימייל <span className="text-red-500" data-id="element-344">*</span>
                          </label>
                          <input type="email" required value={formData.email} onChange={e => handleInputChange('email', e.target.value)} className="w-full px-4 py-3 md:py-4 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all" placeholder="example@email.com" data-id="element-345" />
                        </div>
                      </div>
                    </div>

                    {/* Companion Information (if ticket includes companion) */}
                    {ticketName.includes('+ כרטיס מלווה') && <div className="space-y-4" data-id="element-346">
                        <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-3 pb-3 border-b-2 border-green-100" data-id="element-347">
                          <div className="w-8 h-8 md:w-10 md:h-10 bg-green-100 rounded-full flex items-center justify-center shrink-0" data-id="element-348">
                            <span className="text-green-600 font-bold text-sm md:text-base" data-id="element-349">
                              2
                            </span>
                          </div>
                          פרטי מלווה
                        </h3>

                        <div className="space-y-4 bg-green-50 p-6 rounded-xl border-2 border-green-200" data-id="element-350">
                          <div data-id="element-351">
                            <label className="block text-sm md:text-base font-medium text-gray-700 mb-2" data-id="element-352">
                              שם מלא של המלווה{' '}
                              <span className="text-red-500" data-id="element-353">*</span>
                            </label>
                            <input type="text" required value={formData.companionName} onChange={e => handleInputChange('companionName', e.target.value)} className="w-full px-4 py-3 md:py-4 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all" placeholder="הזן שם מלא של המלווה" data-id="element-354" />
                          </div>

                          <div data-id="element-355">
                            <label className="block text-sm md:text-base font-medium text-gray-700 mb-2" data-id="element-356">
                              טלפון המלווה{' '}
                              <span className="text-red-500" data-id="element-357">*</span>
                            </label>
                            <input type="tel" required value={formData.companionPhone} onChange={e => handleInputChange('companionPhone', e.target.value)} className="w-full px-4 py-3 md:py-4 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all" placeholder="050-1234567" data-id="element-358" />
                          </div>

                          <div data-id="element-359">
                            <label className="block text-sm md:text-base font-medium text-gray-700 mb-3" data-id="element-360">
                              מין המלווה <span className="text-red-500" data-id="element-361">*</span>
                            </label>
                            <div className="flex gap-4" data-id="element-362">
                              <label className="flex items-center gap-2 cursor-pointer" data-id="element-363">
                                <input type="radio" name="companionGender" value="male" checked={formData.companionGender === 'male'} onChange={e => setFormData({
                            ...formData,
                            companionGender: 'male'
                          })} className="w-4 h-4 text-green-600 focus:ring-green-500" data-id="element-364" />
                                <span className="text-sm md:text-base" data-id="element-365">
                                  זכר
                                </span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer" data-id="element-366">
                                <input type="radio" name="companionGender" value="female" checked={formData.companionGender === 'female'} onChange={e => setFormData({
                            ...formData,
                            companionGender: 'female'
                          })} className="w-4 h-4 text-green-600 focus:ring-green-500" data-id="element-367" />
                                <span className="text-sm md:text-base" data-id="element-368">
                                  נקבה
                                </span>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>}

                    {/* Event Details - Accommodation Section (conditional) */}
                    {ticketType === 'accommodation' && <div className="space-y-4" data-id="element-369">
                        <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-3 pb-3 border-b-2 border-indigo-100" data-id="element-370">
                          <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-100 rounded-full flex items-center justify-center shrink-0" data-id="element-371">
                            <span className="text-indigo-600 font-bold text-sm md:text-base" data-id="element-372">
                              2
                            </span>
                          </div>
                          פרטי לינה
                        </h3>

                        <div className="space-y-4" data-id="element-373">
                          {/* Room Type */}
                          <div data-id="element-374">
                            <label className="block text-sm md:text-base font-medium text-gray-700 mb-3" data-id="element-375">
                              סוג החדר <span className="text-red-500" data-id="element-376">*</span>
                            </label>
                            <div className="space-y-2" data-id="element-377">
                              <label className="flex items-center gap-2 cursor-pointer p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-378">
                                <input type="radio" name="roomType" value="double" checked={formData.roomType === 'double'} onChange={e => setFormData({
                            ...formData,
                            roomType: 'double'
                          })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-379" />
                                <span className="text-sm md:text-base" data-id="element-380">
                                  זוגי
                                </span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-381">
                                <input type="radio" name="roomType" value="triple" checked={formData.roomType === 'triple'} onChange={e => setFormData({
                            ...formData,
                            roomType: 'triple'
                          })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-382" />
                                <span className="text-sm md:text-base" data-id="element-383">
                                  חדר פרמיום גדול- טריפל (עד 3 מבוגרים בחדר)
                                </span>
                              </label>
                            </div>
                          </div>

                          {/* Shared Room Acknowledgment - Hide when companion ticket is selected */}
                          {!ticketName.includes('+ כרטיס מלווה') && <label className="flex items-start gap-3 md:gap-4 p-4 md:p-5 bg-amber-50 rounded-xl cursor-pointer hover:bg-amber-100 transition-colors border-2 border-amber-200" data-id="element-384">
                              <input type="checkbox" checked={formData.sharedRoomAcknowledgment} onChange={() => handleCheckboxChange('sharedRoomAcknowledgment')} className="mt-1 w-5 h-5 md:w-6 md:h-6 text-indigo-600 rounded focus:ring-indigo-500 shrink-0" data-id="element-385" />
                              <span className="text-sm md:text-base text-gray-700 leading-relaxed" data-id="element-386">
                                <strong data-id="element-387">
                                  ידוע לי כי הזמנה זו היא עבור לינה בחדר משותף
                                </strong>{' '}
                                - אני מאשר/ת כי מצאתי שותף/ה לחדר מראש,
                                ובאחריותי לוודא שגם השותף/ה מבצע/ת הרשמה והסדרה
                                של התשלום באופן עצמאי{' '}
                                <span className="text-red-500" data-id="element-388">*</span>
                              </span>
                            </label>}

                          {/* Roommate 1 - Hide when companion ticket is selected */}
                          {!ticketName.includes('+ כרטיס מלווה') && <div data-id="element-389">
                              <label className="block text-sm md:text-base font-medium text-gray-700 mb-2" data-id="element-390">
                                שם שותף/ה 1 לחדר{' '}
                                <span className="text-red-500" data-id="element-391">*</span>
                              </label>
                              <input type="text" required={ticketType === 'accommodation' && !ticketName.includes('+ כרטיס מלווה')} value={formData.roommate1Name} onChange={e => handleInputChange('roommate1Name', e.target.value)} className="w-full px-4 py-3 md:py-4 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all" placeholder="שם מלא של שותף/ה" data-id="element-392" />
                            </div>}

                          {/* Roommate 2 (conditional) - Hide when companion ticket is selected */}
                          {formData.roomType === 'triple' && !ticketName.includes('+ כרטיס מלווה') && <div data-id="element-393">
                                <label className="block text-sm md:text-base font-medium text-gray-700 mb-2" data-id="element-394">
                                  רק בחדר טריפל - שם שותף/ה 2 לחדר (אופציונלי)
                                </label>
                                <input type="text" value={formData.roommate2Name} onChange={e => handleInputChange('roommate2Name', e.target.value)} className="w-full px-4 py-3 md:py-4 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all" placeholder="שם מלא של שותף/ה שני (לא חובה)" data-id="element-395" />
                              </div>}
                        </div>
                      </div>}

                    {/* Event Details - Activity Section */}
                    <div className="space-y-4" data-id="element-396">
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-3 pb-3 border-b-2 border-indigo-100" data-id="element-397">
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-100 rounded-full flex items-center justify-center shrink-0" data-id="element-398">
                          <span className="text-indigo-600 font-bold text-sm md:text-base" data-id="element-399">
                            {ticketName.includes('+ כרטיס מלווה') ? ticketType === 'accommodation' ? '4' : ticketType === 'double-alone' ? '3' : '3' : ticketType === 'accommodation' ? '3' : ticketType === 'double-alone' ? '2' : '2'}
                          </span>
                        </div>
                        פרטי השתתפות
                      </h3>

                      <div className="space-y-4" data-id="element-400">
                        {/* Night Choice - Only for "one night" ticket */}
                        {ticketName.includes('לילה אחד') && <div data-id="element-401">
                            <label className="block text-sm md:text-base font-medium text-gray-700 mb-3" data-id="element-402">
                              בחרו לילה לינה:{' '}
                              <span className="text-red-500" data-id="element-403">*</span>
                            </label>
                            <div className="flex gap-4" data-id="element-404">
                              <label className="flex items-center gap-2 cursor-pointer p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors flex-1" data-id="element-405">
                                <input type="radio" name="nightChoice" value="thursday" checked={formData.nightChoice === 'thursday'} onChange={e => setFormData({
                            ...formData,
                            nightChoice: 'thursday'
                          })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-406" />
                                <span className="text-sm md:text-base font-medium" data-id="element-407">
                                  חמישי
                                </span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors flex-1" data-id="element-408">
                                <input type="radio" name="nightChoice" value="friday" checked={formData.nightChoice === 'friday'} onChange={e => setFormData({
                            ...formData,
                            nightChoice: 'friday'
                          })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-409" />
                                <span className="text-sm md:text-base font-medium" data-id="element-410">
                                  שישי
                                </span>
                              </label>
                            </div>
                          </div>}

                        {/* Gender */}
                        <div data-id="element-411">
                          <label className="block text-sm md:text-base font-medium text-gray-700 mb-3" data-id="element-412">
                            מין <span className="text-red-500" data-id="element-413">*</span>
                          </label>
                          <div className="flex gap-4" data-id="element-414">
                            <label className="flex items-center gap-2 cursor-pointer" data-id="element-415">
                              <input type="radio" name="gender" value="male" checked={formData.gender === 'male'} onChange={e => setFormData({
                            ...formData,
                            gender: 'male'
                          })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-416" />
                              <span className="text-sm md:text-base" data-id="element-417">זכר</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer" data-id="element-418">
                              <input type="radio" name="gender" value="female" checked={formData.gender === 'female'} onChange={e => setFormData({
                            ...formData,
                            gender: 'female'
                          })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-419" />
                              <span className="text-sm md:text-base" data-id="element-420">נקבה</span>
                            </label>
                          </div>
                        </div>

                        {/* Competitive Track - Hide for companion-only tickets */}
                        {ticketName !== 'כרטיס מלווה בלבד' && <div data-id="element-421">
                            <label className="block text-sm md:text-base font-medium text-gray-700 mb-3" data-id="element-422">
                              מעוניין להשתתף במסלול הישגי? (ניתן לבחור יותר
                              ממסלול אחד)
                            </label>
                            <div className="space-y-2" data-id="element-423">
                              {[{
                          value: 'no',
                          label: 'לא'
                        }, {
                          value: 'sprint',
                          label: 'מקצה ספרינט'
                        }, {
                          value: 'technical',
                          label: 'מקצה טכני'
                        }, {
                          value: 'longDistance',
                          label: 'מקצה לונג דיסטנס'
                        }].map(option => <label key={option.value} className="flex items-center gap-2 cursor-pointer p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-424">
                                  <input type="checkbox" name="competitiveTrack" value={option.value} checked={formData.competitiveTrack.includes(option.value)} onChange={e => {
                            const value = option.value;
                            setFormData(prev => ({
                              ...prev,
                              competitiveTrack: e.target.checked ? [...prev.competitiveTrack, value] : prev.competitiveTrack.filter(v => v !== value)
                            }));
                          }} className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500" data-id="element-425" />
                                  <span className="text-sm md:text-base" data-id="element-426">
                                    {option.label}
                                  </span>
                                </label>)}
                            </div>
                          </div>}

                        {/* Rowing Type - Hide for companion-only tickets */}
                        {ticketName !== 'כרטיס מלווה בלבד' && <div data-id="element-427">
                            <label className="block text-sm md:text-base font-medium text-gray-700 mb-3" data-id="element-428">
                              חותר ב-
                            </label>
                            <div className="space-y-2" data-id="element-429">
                              {[{
                          value: 'sup',
                          label: 'סאפ'
                        }, {
                          value: 'race',
                          label: 'רייס'
                        }, {
                          value: 'surfski',
                          label: 'סרפסקי'
                        }, {
                          value: 'surfer',
                          label: 'גולש'
                        }, {
                          value: 'atmosphere',
                          label: 'סופג אווירה'
                        }].map(option => <label key={option.value} className="flex items-center gap-2 cursor-pointer p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-430">
                                  <input type="radio" name="rowingType" value={option.value} checked={formData.rowingType === option.value} onChange={e => setFormData({
                            ...formData,
                            rowingType: option.value as any
                          })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-431" />
                                  <span className="text-sm md:text-base" data-id="element-432">
                                    {option.label}
                                  </span>
                                </label>)}
                            </div>
                          </div>}

                        {/* Rowing Club - Hide for companion-only tickets */}
                        {ticketName !== 'כרטיס מלווה בלבד' && <div data-id="element-433">
                            <label className="block text-sm md:text-base font-medium text-gray-700 mb-3" data-id="element-434">
                              שייך למועדון חתירה?
                            </label>
                            <div className="flex gap-4 mb-3" data-id="element-435">
                              <label className="flex items-center gap-2 cursor-pointer" data-id="element-436">
                                <input type="radio" name="belongsToClub" value="yes" checked={formData.belongsToClub === 'yes'} onChange={e => setFormData({
                            ...formData,
                            belongsToClub: 'yes'
                          })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-437" />
                                <span className="text-sm md:text-base" data-id="element-438">כן</span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer" data-id="element-439">
                                <input type="radio" name="belongsToClub" value="no" checked={formData.belongsToClub === 'no'} onChange={e => setFormData({
                            ...formData,
                            belongsToClub: 'no',
                            clubName: ''
                          })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-440" />
                                <span className="text-sm md:text-base" data-id="element-441">לא</span>
                              </label>
                            </div>

                            {formData.belongsToClub === 'yes' && <div data-id="element-442">
                                <label className="block text-sm md:text-base font-medium text-gray-700 mb-2" data-id="element-443">
                                  במידה וכן, לאיזה מועדון?
                                </label>
                                <input type="text" value={formData.clubName} onChange={e => handleInputChange('clubName', e.target.value)} className="w-full px-4 py-3 md:py-4 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all" placeholder="שם המועדון" data-id="element-444" />
                              </div>}
                          </div>}

                        {/* Rent Board - Hide for companion-only tickets */}
                        {ticketName !== 'כרטיס מלווה בלבד' && <div data-id="element-445">
                            <label className="block text-sm md:text-base font-medium text-gray-700 mb-3" data-id="element-446">
                              מעוניין להשכיר גלשן לאירוע?
                            </label>
                            <div className="flex gap-4" data-id="element-447">
                              <label className="flex items-center gap-2 cursor-pointer" data-id="element-448">
                                <input type="radio" name="rentBoard" value="yes" checked={formData.rentBoard === 'yes'} onChange={e => setFormData({
                            ...formData,
                            rentBoard: 'yes'
                          })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-449" />
                                <span className="text-sm md:text-base" data-id="element-450">כן</span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer" data-id="element-451">
                                <input type="radio" name="rentBoard" value="no" checked={formData.rentBoard === 'no'} onChange={e => setFormData({
                            ...formData,
                            rentBoard: 'no'
                          })} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" data-id="element-452" />
                                <span className="text-sm md:text-base" data-id="element-453">לא</span>
                              </label>
                            </div>
                          </div>}
                      </div>
                    </div>

                    {/* Health & Safety Declarations */}
                    <div className="space-y-4" data-id="element-454">
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-3 pb-3 border-b-2 border-indigo-100" data-id="element-455">
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-100 rounded-full flex items-center justify-center shrink-0" data-id="element-456">
                          <span className="text-indigo-600 font-bold text-sm md:text-base" data-id="element-457">
                            {ticketName.includes('+ כרטיס מלווה') ? ticketType === 'accommodation' ? '5' : ticketType === 'double-alone' ? '4' : '4' : ticketType === 'accommodation' ? '4' : ticketType === 'double-alone' ? '3' : '3'}
                          </span>
                        </div>
                        הצהרות בריאות ובטיחות
                      </h3>

                      <div className="space-y-3" data-id="element-458">
                        <label className="flex items-start gap-3 md:gap-4 p-4 md:p-5 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors border-2 border-transparent hover:border-indigo-100" data-id="element-459">
                          <input type="checkbox" checked={formData.healthDeclaration} onChange={() => handleCheckboxChange('healthDeclaration')} className="mt-1 w-5 h-5 md:w-6 md:h-6 text-indigo-600 rounded focus:ring-indigo-500 shrink-0" data-id="element-460" />
                          <span className="text-sm md:text-base text-gray-700 leading-relaxed" data-id="element-461">
                            אני מצהיר/ה כי אני בריא/ה, בכושר גופני מתאים, וכי
                            אין לי כל מגבלה רפואית או גופנית המונעת ממני להשתתף
                            בפעילות הספורט ו/או במקצים השונים באירוע.
                          </span>
                        </label>

                        <label className="flex items-start gap-3 md:gap-4 p-4 md:p-5 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors border-2 border-transparent hover:border-indigo-100" data-id="element-462">
                          <input type="checkbox" checked={formData.medicalCheckups} onChange={() => handleCheckboxChange('medicalCheckups')} className="mt-1 w-5 h-5 md:w-6 md:h-6 text-indigo-600 rounded focus:ring-indigo-500 shrink-0" data-id="element-463" />
                          <span className="text-sm md:text-base text-gray-700 leading-relaxed" data-id="element-464">
                            ידוע לי כי על פי חוק הספורט, האחריות לביצוע בדיקות
                            רפואיות תקופתיות חלה עליי, ואני מאשר/ת כי עשיתי כן
                            וכי נמצאתי כשיר/ה לפעילות המתוכננת.
                          </span>
                        </label>

                        <label className="flex items-start gap-3 md:gap-4 p-4 md:p-5 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors border-2 border-transparent hover:border-indigo-100" data-id="element-465">
                          <input type="checkbox" checked={formData.riskAwareness} onChange={() => handleCheckboxChange('riskAwareness')} className="mt-1 w-5 h-5 md:w-6 md:h-6 text-indigo-600 rounded focus:ring-indigo-500 shrink-0" data-id="element-466" />
                          <span className="text-sm md:text-base text-gray-700 leading-relaxed" data-id="element-467">
                            אני מודע/ת לכך שהשתתפות בפעילות ספורטיבית כרוכה
                            במאמץ גופני ובסיכונים פציעה אינהרנטיים. אני בוחר/ת
                            להשתתף בפעילות מרצוני החופשי ולאחר ששקלתי את
                            הסיכונים.
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Liability Waiver */}
                    <div className="space-y-4" data-id="element-468">
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-3 pb-3 border-b-2 border-indigo-100" data-id="element-469">
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-100 rounded-full flex items-center justify-center shrink-0" data-id="element-470">
                          <span className="text-indigo-600 font-bold text-sm md:text-base" data-id="element-471">
                            {ticketName.includes('+ כרטיס מלווה') ? ticketType === 'accommodation' ? '6' : ticketType === 'double-alone' ? '5' : '5' : ticketType === 'accommodation' ? '5' : ticketType === 'double-alone' ? '4' : '4'}
                          </span>
                        </div>
                        שחרור מאחריות
                      </h3>

                      <div className="space-y-3" data-id="element-472">
                        <label className="flex items-start gap-3 md:gap-4 p-4 md:p-5 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors border-2 border-transparent hover:border-indigo-100" data-id="element-473">
                          <input type="checkbox" checked={formData.liabilityWaiver} onChange={() => handleCheckboxChange('liabilityWaiver')} className="mt-1 w-5 h-5 md:w-6 md:h-6 text-indigo-600 rounded focus:ring-indigo-500 shrink-0" data-id="element-474" />
                          <span className="text-sm md:text-base text-gray-700 leading-relaxed" data-id="element-475">
                            אני מוותר/ת בזאת באופן מלא ומוחלט על כל טענה, דרישה
                            או תביעה כנגד מארגני האירוע, המדריכים, נותני החסות
                            או בעלי המקום, בגין כל נזק גוף, נזק רכוש או אובדן
                            שייגרם לי במהלך האירוע או כתוצאה ממנו, למעט במקרים
                            של רשלנות פושעת מצד המארגנים.
                          </span>
                        </label>

                        <label className="flex items-start gap-3 md:gap-4 p-4 md:p-5 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors border-2 border-transparent hover:border-indigo-100" data-id="element-476">
                          <input type="checkbox" checked={formData.medicalTreatment} onChange={() => handleCheckboxChange('medicalTreatment')} className="mt-1 w-5 h-5 md:w-6 md:h-6 text-indigo-600 rounded focus:ring-indigo-500 shrink-0" data-id="element-477" />
                          <span className="text-sm md:text-base text-gray-700 leading-relaxed" data-id="element-478">
                            אני מאשר/ת למארגנים להעניק לי עזרה ראשונה או לפנות
                            אותי לטיפול רפואי במידת הצורך, ומסכים/ה לשאת בעלויות
                            הכרוכות בכך (כגון אמבולנס) ככל שיידרש.
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Photography & Media */}
                    <div className="space-y-4" data-id="element-479">
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-3 pb-3 border-b-2 border-indigo-100" data-id="element-480">
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-100 rounded-full flex items-center justify-center shrink-0" data-id="element-481">
                          <span className="text-indigo-600 font-bold text-sm md:text-base" data-id="element-482">
                            {ticketName.includes('+ כרטיס מלווה') ? ticketType === 'accommodation' ? '7' : ticketType === 'double-alone' ? '6' : '6' : ticketType === 'accommodation' ? '6' : ticketType === 'double-alone' ? '5' : '5'}
                          </span>
                        </div>
                        צילום ופרסום
                      </h3>

                      <label className="flex items-start gap-3 md:gap-4 p-4 md:p-5 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors border-2 border-transparent hover:border-indigo-100" data-id="element-483">
                        <input type="checkbox" checked={formData.photographyConsent} onChange={() => handleCheckboxChange('photographyConsent')} className="mt-1 w-5 h-5 md:w-6 md:h-6 text-indigo-600 rounded focus:ring-indigo-500 shrink-0" data-id="element-484" />
                        <span className="text-sm md:text-base text-gray-700 leading-relaxed" data-id="element-485">
                          אני נותן/ת בזאת את הסכמתי למארגני האירוע או למי מטעמם,
                          לצלם אותי במהלך הפעילות. אני מסכים/ה כי המארגנים יוכלו
                          לעשות שימוש בתמונות ובסרטונים אלו לצרכי תיעוד, פרסום,
                          שיווק ויחסי ציבור של האירוע, בכל מדיה, ללא הגבלת זמן
                          וללא תמורה כספית.
                        </span>
                      </label>
                    </div>

                    {/* Cancellation Policy */}
                    <div className="space-y-4" data-id="element-486">
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-3 pb-3 border-b-2 border-indigo-100" data-id="element-487">
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-100 rounded-full flex items-center justify-center shrink-0" data-id="element-488">
                          <span className="text-indigo-600 font-bold text-sm md:text-base" data-id="element-489">
                            {ticketName.includes('+ כרטיס מלווה') ? ticketType === 'accommodation' ? '8' : ticketType === 'double-alone' ? '7' : '7' : ticketType === 'accommodation' ? '7' : ticketType === 'double-alone' ? '6' : '6'}
                          </span>
                        </div>
                        מדיניות ביטולים
                      </h3>

                      <div className="border-2 border-gray-200 rounded-xl overflow-hidden" data-id="element-490">
                        <button type="button" onClick={() => setShowCancellationPolicy(!showCancellationPolicy)} className="w-full flex items-center justify-between p-4 md:p-5 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-491">
                          <span className="font-medium text-gray-900 text-sm md:text-base" data-id="element-492">
                            לחץ לקריאת מדיניות הביטולים
                          </span>
                          {showCancellationPolicy ? <ChevronUp className="w-5 h-5 md:w-6 md:h-6 text-gray-600 shrink-0" data-id="element-493" /> : <ChevronDown className="w-5 h-5 md:w-6 md:h-6 text-gray-600 shrink-0" data-id="element-494" />}
                        </button>

                        {showCancellationPolicy && <div className="p-4 md:p-5 bg-white space-y-3 text-sm md:text-base text-gray-700 border-t-2 border-gray-200" data-id="element-495">
                            <p className="font-medium" data-id="element-496">תנאי ביטול:</p>
                            <ul className="list-disc list-inside space-y-2 mr-4" data-id="element-497">
                              <li data-id="element-498">
                                ביטול בין שלושה שבועות לשבועיים לפני מועד האירוע
                                – יחויבו 50% מעלות ההשתתפות
                              </li>
                              <li data-id="element-499">
                                ביטול עד שבוע לפני מועד האירוע – יחויבו 70%
                                מעלות ההשתתפות
                              </li>
                              <li data-id="element-500">
                                ביטול במהלך השבוע שלפני מועד האירוע (7 ימים
                                ומטה) – יחויבו 100% מעלות ההשתתפות
                              </li>
                            </ul>
                          </div>}
                      </div>

                      <label className="flex items-start gap-3 md:gap-4 p-4 md:p-5 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors border-2 border-transparent hover:border-indigo-100" data-id="element-501">
                        <input type="checkbox" checked={formData.cancellationPolicy} onChange={() => handleCheckboxChange('cancellationPolicy')} className="mt-1 w-5 h-5 md:w-6 md:h-6 text-indigo-600 rounded focus:ring-indigo-500 shrink-0" data-id="element-502" />
                        <span className="text-sm md:text-base text-gray-700 leading-relaxed font-medium" data-id="element-503">
                          קראתי והבנתי את מדיניות הביטולים
                        </span>
                      </label>
                    </div>

                    {/* WhatsApp Group (Optional) */}
                    <div className="space-y-4" data-id="element-504">
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-3 pb-3 border-b-2 border-green-100" data-id="element-505">
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-green-100 rounded-full flex items-center justify-center shrink-0" data-id="element-506">
                          <span className="text-green-600 font-bold text-sm md:text-base" data-id="element-507">
                            {ticketName.includes('+ כרטיס מלווה') ? ticketType === 'accommodation' ? '9' : ticketType === 'double-alone' ? '8' : '8' : ticketType === 'accommodation' ? '8' : ticketType === 'double-alone' ? '7' : '7'}
                          </span>
                        </div>
                        קבוצת וואטסאפ (אופציונלי)
                      </h3>

                      <label className="flex items-start gap-3 md:gap-4 p-4 md:p-5 bg-green-50 rounded-xl cursor-pointer hover:bg-green-100 transition-colors border-2 border-transparent hover:border-green-200" data-id="element-508">
                        <input type="checkbox" checked={formData.whatsappGroup} onChange={() => handleCheckboxChange('whatsappGroup')} className="mt-1 w-5 h-5 md:w-6 md:h-6 text-green-600 rounded focus:ring-green-500 shrink-0" data-id="element-509" />
                        <span className="text-sm md:text-base text-gray-700 leading-relaxed" data-id="element-510">
                          אני מעוניין/ת שתוסיפו אותי לקבוצת הוואטסאפ השקטה של
                          האירוע
                        </span>
                      </label>
                    </div>

                    {/* Submit Button */}
                    <div className="sticky bottom-0 bg-white pt-6 pb-2 border-t-2 border-gray-200 -mx-6 md:-mx-8 px-6 md:px-8" data-id="element-511">
                      {!isFormValid() && <div className="flex items-start gap-3 mb-4 p-4 bg-amber-50 border-2 border-amber-200 rounded-xl" data-id="element-512">
                          <AlertCircle className="w-5 h-5 md:w-6 md:h-6 text-amber-600 shrink-0 mt-0.5" data-id="element-513" />
                          <p className="text-sm md:text-base text-amber-800 leading-relaxed" data-id="element-514">
                            יש למלא את כל השדות ולאשר את כל ההצהרות הנדרשות לפני
                            המעבר לתשלום
                          </p>
                        </div>}

                      <Button type="submit" disabled={!isFormValid()} className={`w-full py-4 md:py-5 text-base md:text-lg font-bold rounded-xl transition-all ${isFormValid() ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`} data-id="element-515">
                        {isFormValid() ? <span className="flex items-center justify-center gap-2" data-id="element-516">
                            <CheckCircle2 className="w-5 h-5 md:w-6 md:h-6" data-id="element-517" />
                            המשך לתשלום
                          </span> : 'נא למלא את כל השדות'}
                      </Button>
                    </div>
                  </form>
                </div>
              </motion.div>
            </div>
          </div>
        </>}
    </AnimatePresence>;
}